import { useCatalogueRevision } from './catalogueRevision';
import { useCallback, useEffect, useState } from 'react';

/** A shared read as a page holds it (the skill, card and tool libraries): `data` once read, `error` once it failed,
 *  neither while it is read. Its loader reads once for the app and asks again after a failure: on `retry`, or for
 *  the next page that wants it. */
export interface Read<T> { data: T | null; error: string | null; retry: () => void }

/** `load`, read once it is `wanted` (a page that may never need it reads nothing until it does). `load` is a loader
 *  changes only when what it reads does: a module's loader, or one kept by useCallback on its key. */
export function useRead<T>(load: () => Promise<T>, wanted = true): Read<T> {
  const catalogueRevision = useCatalogueRevision();
  const [state, setState] = useState<{ data: T | null; error: string | null }>({ data: null, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!wanted) return undefined;
    let alive = true;
    // a different read (another card's tools), or the same asked again: nothing stale stands while it is read
    setState((s) => (s.data === null && s.error === null ? s : { data: null, error: null }));
    load().then((data) => { if (alive) setState({ data, error: null }); },
      (e: unknown) => { if (alive) setState({ data: null, error: e instanceof Error ? e.message : String(e) }); });
    return () => { alive = false; };
  }, [load, wanted, attempt, catalogueRevision]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}
